<script setup>
import { router } from '@inertiajs/vue3'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  user: { type: Object, required: true },
  guards: { type: Object, default: () => ({ is_self: false, is_last_admin: false }) },
})

const archiveDisabled = computed(() => props.guards.is_self || props.guards.is_last_admin)
const restoreDisabled = computed(() => props.guards.is_self)
const forceDeleteDisabled = computed(() => props.guards.is_self || props.guards.is_last_admin)

const archiveHint = computed(() => {
  if (props.guards.is_self) return 'messages.admin.user.cannot_archive_self_hint'
  if (props.guards.is_last_admin) return 'messages.admin.user.cannot_archive_last_admin_hint'
  return null
})
const forceDeleteHint = computed(() => {
  if (props.guards.is_self) return 'messages.admin.user.cannot_force_delete_self_hint'
  if (props.guards.is_last_admin) return 'messages.admin.user.cannot_force_delete_last_admin_hint'
  return null
})

const { t } = useI18n()
const toast = useToast()
const confirm = useConfirm()

const showForceDeleteDialog = ref(false)
const typedEmail = ref('')

const archive = () => {
  confirm.require({
    message: t('messages.admin.user.archive_confirm', {
      name: props.user.firstname + ' ' + props.user.lastname,
    }),
    header: t('messages.admin.user.archive_confirm_header'),
    accept: () => {
      router.delete(route('admin.user.destroy', props.user.id), {
        onSuccess: () => {
          toast.add({
            severity: 'success',
            summary: t('messages.updated'),
            detail: t('messages.admin.user.archive_success', { name: props.user.full_name }),
            life: 3000,
          })
        },
      })
    },
  })
}

const restore = () => {
  router.post(route('admin.user.restore', props.user.id), {}, {
    preserveScroll: true,
    onSuccess: () => {
      toast.add({
        severity: 'success',
        summary: t('messages.updated'),
        detail: t('messages.admin.user.restored_success'),
        life: 3000,
      })
    },
  })
}

const openForceDelete = () => {
  typedEmail.value = ''
  showForceDeleteDialog.value = true
}

const forceDelete = () => {
  router.delete(route('admin.user.force-delete', props.user.id), {
    onSuccess: () => {
      showForceDeleteDialog.value = false
      toast.add({
        severity: 'success',
        summary: t('messages.updated'),
        detail: t('messages.admin.user.force_deleted_success'),
        life: 3000,
      })
    },
    onError: () => {
      toast.add({
        severity: 'error',
        summary: t('messages.error'),
        detail: t('messages.admin.user.force_delete_blocked_by_fks'),
        life: 5000,
      })
    },
  })
}
</script>

<template>
  <Card :pt="{ root: { class: 'border border-red-300' } }">
    <template #title>
      <span class="text-red-700">{{ $t('messages.admin.user.tabs.danger') }}</span>
    </template>
    <template #content>
      <div class="flex flex-col gap-3">
        <div v-if="!user.deleted_at" class="flex items-center justify-between gap-4">
          <div class="flex flex-col text-sm text-gray-600">
            <span>{{ $t('messages.admin.user.archive_desc') }}</span>
            <span v-if="archiveHint" class="text-xs text-gray-400 italic">{{ $t(archiveHint) }}</span>
          </div>
          <Button severity="danger" outlined :disabled="archiveDisabled" @click="archive">
            {{ $t('messages.admin.user.archive') }}
          </Button>
        </div>
        <div v-if="user.deleted_at" class="flex items-center justify-between gap-4">
          <div class="text-sm text-gray-600">{{ $t('messages.admin.user.restore_desc') }}</div>
          <Button outlined :disabled="restoreDisabled" @click="restore">
            {{ $t('messages.admin.user.restore') }}
          </Button>
        </div>
        <div v-if="user.deleted_at" class="flex items-center justify-between gap-4">
          <div class="flex flex-col text-sm text-gray-600">
            <span>{{ $t('messages.admin.user.force_delete_desc') }}</span>
            <span v-if="forceDeleteHint" class="text-xs text-gray-400 italic">{{ $t(forceDeleteHint) }}</span>
          </div>
          <Button severity="danger" :disabled="forceDeleteDisabled" @click="openForceDelete">
            {{ $t('messages.admin.user.force_delete') }}
          </Button>
        </div>
      </div>
    </template>
  </Card>

  <Dialog
    v-model:visible="showForceDeleteDialog"
    :header="$t('messages.admin.user.force_delete_confirm')"
    modal
    class="w-full max-w-md"
  >
    <p class="mb-2">{{ $t('messages.admin.user.force_delete_type_email', { email: user.email }) }}</p>
    <Fluid>
      <InputText v-model="typedEmail" :placeholder="user.email" />
    </Fluid>
    <template #footer>
      <Button outlined :label="$t('messages.cancel')" @click="showForceDeleteDialog = false" />
      <Button
        severity="danger"
        :label="$t('messages.admin.user.force_delete')"
        :disabled="typedEmail !== user.email"
        @click="forceDelete"
      />
    </template>
  </Dialog>
</template>
